// Lib
import { mapState } from "vuex";

// Event, Dialog
import EventBus from "@/event/EventBus";

// Api
import api from '@/api/baseApi';

// Constant
import { SETTINGS_RENTAL_API, SETTINGS_FIXED_RENTAL_API } from "@/constant/apiSettings";
import { POPUP_ID } from "@/constant/baseConstant";
import { API_CALL_STATUS } from "@/constant/baseConstant";
import { BOOT_MACHINE_STATUS, RENTAL_REQUEST_TYPE } from "@/constant/rental";
import { PERIOD, FIXED_RENTAL_STATUS, LIST_PACKAGE_REQUIRED_FIXED_RENTAL } from "@/constant/fixedRental";
import FixedRentalSettings from "@/views/component/FixedRental/FixedRentalSettings";

// Extend
import baseRentalService from '@/scripts/rentalService/baseRentalService';

// Components  
import FixedRentalExpired from "@/views/component/RentalService/Notification/FixedRentalExpired";
import FixedRentalTooEarly from "@/views/component/RentalService/Notification/FixedRentalTooEarly";
import NoDeposit from "@/views/component/RentalService/Notification/NoDeposit";
import MacUsed from "@/views/component/RentalService/Notification/MacUsed";
import FixedRentalOnly from "@/views/component/RentalService/Notification/FixedRentalOnly";
import MachineNotReadyStart from "@/views/component/FixedRental/Notification/MachineNotReadyStart";

export default {

    extends: baseRentalService,

    components: {
        FixedRentalSettings,
        FixedRentalExpired,
        FixedRentalTooEarly,
        MachineNotReadyStart,
        NoDeposit,
        MacUsed,
        FixedRentalOnly,
    },

    data() {

        return {
            imgDict: {
                1: "dot-green",
                2: "dot-blue",
                3: "dot-orange",
                4: "dot-red",
            },
            colorDict: {
                1: "#4CAF50",
                2: "#2196F3",
                3: "#E65100",
                4: "#F44336",
            },
            bootMachineStatus: BOOT_MACHINE_STATUS,
            fixedRentalStatus: FIXED_RENTAL_STATUS,

            request: {
                campaignName: null,
                period: PERIOD.THREE_HOUR,
                periodUnit: 1,
                totalCost: 0,
                continueRunning: false,
            },
            conti: false,
            isBooting: false,
            bootingCheckInterval: null, // Interval để kiểm tra trạng thái booting
            packageRequiredFixedRental: LIST_PACKAGE_REQUIRED_FIXED_RENTAL,
        };
    },

    computed: {
        ...mapState({
            serverReport: (state) => state.common.serverReport,
            tempBalance: state => state.auth.tempBalance,
        }),
        dataNote() {
            return [
                {
                    label: this.$t('bootMachine.noteTitle'),
                    children: [
                        {
                            label: this.$t('bootMachine.noteZDrive')
                        },
                        {
                            label: this.$t('bootMachine.noteDeactivateLicense')
                        }
                    ]
                },
            ];
        },
        getColorKey() {
            if (this.targetImage) {
                try {
                    return this.targetImage.operatingSystem === 1
                        ? this.serverReport
                            .find((x) => x.packageId === this.targetImage.packageId)
                            .statusWindows.toString()
                        : this.serverReport
                            .find((x) => x.packageId === this.targetImage.packageId)
                            .statusUbuntu.toString();
                }
                catch {
                    return "4";
                }
            }
            else {
                return "4";
            }
        },
        getStatusText() {
            if (this.targetImage) {
                try {
                    return this.targetImage.operatingSystem === 1
                        ? this.serverReport.find(
                            (x) => x.packageId === this.targetImage.packageId
                        ).statusTextWindows
                        : this.serverReport.find(
                            (x) => x.packageId === this.targetImage.packageId
                        ).statusTextUbuntu;
                }
                catch {
                    return "full";
                }
            }
            else {
                return "full";
            }
        }
    },

    props: {
        targetImage: {
            type: Object,
            default: null,
        },
    },
    watch: {
        targetImage() {
            if(this.targetImage !== null && this.targetImage.packageId !== undefined && this.targetImage.fixedRental !== undefined)
            {
                if (this.packageRequiredFixedRental.includes(this.targetImage.packageId) && !this.targetImage.fixedRental) {
                    this.conti = true;
                }
            }
        }
    },
    mounted() {
        // Dọn dẹp các trạng thái booting hết hạn trước
        this.cleanupExpiredBootingStates();
        
        // Chờ targetImage được load xúng trước khi kiểm tra
        this.$nextTick(() => {
            if (this.targetImage && this.targetImage.id) {
                // Kiểm tra trạng thái booting từ localStorage cho machine này
                const isBootingFromStorage = this.getBootingState(this.targetImage.id);
                
                if (isBootingFromStorage) {
                    // Nếu đang booting, hiển thị loading
                    this.feedStatusLoading(API_CALL_STATUS.LOADING);
                    
                    // Kiểm tra nếu targetImage đã có fixedRental thì clear booting state
                    if (this.targetImage.fixedRental) {
                        this.clearBootingState(this.targetImage.id);
                        this.feedStatusLoading(API_CALL_STATUS.SUCCESS);
                    } else {
                        // Nếu chưa có fixedRental, giữ trạng thái booting và bắt đầu kiểm tra
                        this.isBooting = true;
                        this.startBootingStateCheck();
                    }
                }
            }
        });
        
        if(this.targetImage !== null && this.targetImage.packageId !== undefined && this.targetImage.fixedRental !== undefined)
        {
            if (this.packageRequiredFixedRental.includes(this.targetImage.packageId) && !this.targetImage.fixedRental) {
                this.conti = true;
            }
        }
    },
    
    beforeDestroy() {
        // Cleanup interval khi component bị hủy
        this.stopBootingStateCheck();
    },

    methods: {

        //#region localStorage helper for booting state
        getBootingState(machineId = null) {
            const id = machineId || (this.targetImage && this.targetImage.id);
            if (!id) return false;
            
            const bootingData = localStorage.getItem(`isBootingFixedRental_${id}`);
            if (!bootingData) return false;
            
            try {
                const data = JSON.parse(bootingData);
                const currentTime = new Date().getTime();
                const bootingTime = new Date(data.timestamp).getTime();
                const timeDiffMinutes = (currentTime - bootingTime) / (1000 * 60);
                
                // Nếu quá 2 phút thì tự động clear
                if (timeDiffMinutes > 2) {
                    this.clearBootingState(machineId);
                    return false;
                }
                
                return data.isBooting === true;
            } catch (error) {
                // Nếu data bị lỗi thì clear và return false
                this.clearBootingState(machineId);
                return false;
            }
        },
        
        setBootingState(isBooting, machineId = null) {
            const id = machineId || (this.targetImage && this.targetImage.id);
            if (!id) return;
            
            if (isBooting) {
                // Lưu cả trạng thái và timestamp
                const bootingData = {
                    isBooting: true,
                    timestamp: new Date().toISOString(),
                    machineId: id
                };
                localStorage.setItem(`isBootingFixedRental_${id}`, JSON.stringify(bootingData));
            } else {
                // Nếu set false thì xóa luôn
                this.clearBootingState(machineId);
            }
            
            this.isBooting = isBooting;
        },
        
        clearBootingState(machineId = null) {
            const id = machineId || (this.targetImage && this.targetImage.id);
            if (!id) return;
            localStorage.removeItem(`isBootingFixedRental_${id}`);
            this.isBooting = false;
            
            // Dừng kiểm tra khi clear booting state
            this.stopBootingStateCheck();
        },
        
        // Hàm helper để dọn dẹp tất cả trạng thái booting hết hạn
        cleanupExpiredBootingStates() {
            const currentTime = new Date().getTime();
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('isBootingFixedRental_')) {
                    try {
                        const data = JSON.parse(localStorage.getItem(key));
                        const bootingTime = new Date(data.timestamp).getTime();
                        const timeDiffMinutes = (currentTime - bootingTime) / (1000 * 60);
                        
                        if (timeDiffMinutes > 2) {
                            localStorage.removeItem(key);
                            console.log(`Cleaned up expired booting state for key: ${key}`);
                        }
                    } catch (error) {
                        // Nếu data bị lỗi thì xóa luôn
                        localStorage.removeItem(key);
                        console.log(`Cleaned up corrupted booting state for key: ${key}`);
                    }
                }
            });
        },
        
        // Bắt đầu kiểm tra trạng thái booting mỗi 1 giây
        startBootingStateCheck() {
            // Clear interval cũ nếu có
            this.stopBootingStateCheck();
            
            this.bootingCheckInterval = setInterval(() => {
                if (this.targetImage && this.targetImage.id) {
                    const isStillBooting = this.getBootingState(this.targetImage.id);
                    
                    if (!isStillBooting && this.isBooting) {
                        // Nếu trạng thái đã bị clear (do timeout hoặc lý do khác)
                        console.log(`Booting state cleared for machine #${this.targetImage.id}, stopping loading...`);
                        this.isBooting = false;
                        this.feedStatusLoading(API_CALL_STATUS.SUCCESS);
                        this.stopBootingStateCheck();
                    }
                }
            }, 1000); // Kiểm tra mỗi 1 giây
        },
        
        // Dừng kiểm tra trạng thái booting
        stopBootingStateCheck() {
            if (this.bootingCheckInterval) {
                clearInterval(this.bootingCheckInterval);
                this.bootingCheckInterval = null;
            }
        },
        //#endregion

        //#region Processing event
        processingEvent(param) {
            this[param.event](param.data);
        },
        //#endregion

        //#region Close this pop-up
        closeThisPopup() {
            this.closePopup(POPUP_ID.BOOT_MACHINE);
        },
        //#endregion

        //#region Boot machine  
        boot() {
            // Kiểm tra nếu đang trong quá trình boot thì không thực hiện
            if (this.getBootingState(this.targetImage.id)) {
                console.log(`Boot process for machine #${this.targetImage.id} is already in progress. Please wait...`);
                return;
            }
            
            // Đặt cờ đang boot vào localStorage cho machine này
            this.setBootingState(true, this.targetImage.id);
            
            this.feedStatusLoading(API_CALL_STATUS.LOADING);
            
            if (this.targetImage.fixedRental && !this.targetImagefixedRentalExpired) {
                return this.boot1();
            }

            if (this.conti) {
                setTimeout(() => {
                    let data = {
                        campaignName: this.request.campaignName,
                        listMachineId: [this.targetImage.id],
                        // fixedRental: this.targetImage.fixedRental,
                        period: this.request.period,
                        periodUnit: this.request.periodUnit,
                        continueRunning: this.request.continueRunning,
                        deviceInformation: JSON.stringify(this.getInformationClient())
                    }
                    api.baseRequest(SETTINGS_FIXED_RENTAL_API.FIXED_RENTAL_CREATE, data)
                        .then((res) => {
                            switch (res.errorCode) {
                                case FIXED_RENTAL_STATUS.SUCCESS:
                                    EventBus.$emit("processingEventRental", { event: "isLoadingElement", data: [this.targetImage.id] });
                                    EventBus.$emit("processingEventRental", { event: "autoRefresh" });
                                    this.notificationSuccess(this.$t('bootMachine.rentalPlanSuccessTitle'), this.$t('bootMachine.rentalPlanSuccessMessage'));
                                    this.clearBootingState(this.targetImage.id);
                                    this.boot1();
                                    break;
                                case FIXED_RENTAL_STATUS.MACHINE_NOT_READY:
                                case FIXED_RENTAL_STATUS.BALANCE_NOT_ENOUGH:
                                case FIXED_RENTAL_STATUS.MACHINE_NOT_AVAILABLE:
                                    setTimeout(() => {
                                        this.isShowErrorInformation = true;
                                    }, 500);
                                    this.errorCode = res.errorCode
                                    this.clearBootingState(this.targetImage.id);
                                    this.feedStatusLoading(API_CALL_STATUS.SUCCESS);
                                    break;
                            }
                        })
                        .catch(error => {
                            this.clearBootingState(this.targetImage.id);
                            this.processCatchApiRequest(error, this.$t('bootMachine.rentalPlanFailed'))
                        });
                }, this.smoothTimeOut);
            } else {
                this.boot1();
            }
        },
        //#endregion

        boot1() {
            setTimeout(() => {
                let data = {
                    machineId: this.targetImage.id,
                    deviceInformation: JSON.stringify(this.getInformationClient())
                }
                api.baseRequest(SETTINGS_RENTAL_API.BOOT_MACHINE, data)
                    .then((res) => {
                        switch (res.errorCode) {
                            case BOOT_MACHINE_STATUS.SUCCESS:
                                EventBus.$emit("processingEventRental", { event: "isLoadingElement", data: [this.targetImage.id] });
                                EventBus.$emit("processingEventRental", { event: "autoRefresh" });
                                this.closeThisPopup();
                                this.notificationSuccess(this.$t('bootMachine.bootingTitle'), this.$t('bootMachine.bootingMessage', { id: this.targetImage.id, name: this.targetImage.name }));
                                this.clearBootingState(this.targetImage.id);
                                break;
                            case BOOT_MACHINE_STATUS.FIXED_RENTAL_TOO_EARLY:
                            case BOOT_MACHINE_STATUS.FIXED_RENTAL_EXPIRED:
                            case BOOT_MACHINE_STATUS.BALANCE_NOT_ENOUGH:
                            case BOOT_MACHINE_STATUS.TOTAL_RECHARGE_NOT_ENOUGH:
                            case BOOT_MACHINE_STATUS.MACHINE_NOT_AVAILABLE:
                            case BOOT_MACHINE_STATUS.MAC_USED:
                            case BOOT_MACHINE_STATUS.FIXED_RENTAL_ONLY:
                                setTimeout(() => {
                                    this.isShowErrorInformation = true;
                                }, 500);
                                this.errorCode = res.errorCode
                                this.clearBootingState(this.targetImage.id);
                                break;
                        }
                        this.feedStatusLoading(API_CALL_STATUS.SUCCESS);
                    })
                    .catch(error => {
                        this.clearBootingState(this.targetImage.id);
                        this.processCatchApiRequest(error, this.$t('bootMachine.bootFailed'))
                    });
            }, 1000);
        },

        //#region Rental request create
        rentalRequestCreate() {
            this.feedStatusLoading(API_CALL_STATUS.LOADING);
            setTimeout(() => {
                let data = {
                    type: RENTAL_REQUEST_TYPE.NOTIFY_MACHINE_AVAILABLE,
                    packageId: this.targetImage.packageId,
                    operatingSystem: this.targetImage.operatingSystem,
                    deviceInformation: JSON.stringify(this.getInformationClient())
                }
                this.baseRentalRequestCreate(
                    data,
                    this.targetImage,
                    () => { this.closeThisPopup() }
                )
            }, 1000);
        },
        //#endregion
    },
}